import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import SignIn from './auth/signIn';
import VerifyOtp from './auth/VerifyOtp';
import Dashboard from './components/Dashboard/Dashboard';
import Navbar from './components/common/Navbar';
import Sidebar from './components/common/Sidebar';
import './App.css';
import ClientDashboard from './components/Dashboard/ClientDashboard.js/ClientDashboardMain';
import Maintenance from './components/Dashboard/Maintainence';
import ForgotPassword from './auth/ForgotPassword';
import VerifyOtpForgot from './auth/VerifyOtpForgot';
import ResetPassword from './auth/ResetPassword';
import TalliesSidebar from './components/Dashboard/TalliesSidebar';
import JobsModule from './components/Dashboard/JobsModule';
import JobDetails from './components/Dashboard/JobDetails';
import PackageCostsDetails from './components/Dashboard/PackageCostsDetails';
import PostageDetails from './components/Dashboard/PostageDetails';
import BackendCostsDetails from './components/Dashboard/BackendCostsDetails';
import Lists from './components/Dashboard/Lists';
import { RefreshToken } from './services/accountService';
import Reports from './components/Dashboard/Reports';
import SuccessMessage from './components/common/SuccessMessage';
import ProtectedRoute from './utils/ProtectedRoute';
import Unauthorized from './utils/Unauthorized';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSidebarItem, setActiveSidebarItem] = useState('client');

  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(null);
  const [warningTimer, setWarningTimer] = useState(null);

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const popupShownRef = useRef(false);

  useEffect(() => {

    const SESSION_TIME = 30 * 60 * 60 * 1000;   // 30 hours
    const WARNING_TIME = SESSION_TIME - (5 * 60 * 1000); // last 5 mins

    let logoutTimer;
    let popupTimer;

    const startTimer = () => {
      clearTimeout(logoutTimer);
      clearTimeout(popupTimer);

      popupTimer = setTimeout(() => {
        if (!popupShownRef.current) {
          console.log("Showing session expiry warning");
          setShowSessionPopup(true);
          popupShownRef.current = true;
        }
      }, WARNING_TIME);

      logoutTimer = setTimeout(() => {
        console.log("Session expired → logout");
        handleLogout();
      }, SESSION_TIME);
    };

    startTimer();

    const events = ['click', 'mousemove', 'keydown', 'scroll'];

    const resetTimer = () => {
      if (!popupShownRef.current) {
        startTimer();
      }
    };

    events.forEach(event =>
      window.addEventListener(event, resetTimer)
    );

    return () => {
      clearTimeout(logoutTimer);
      clearTimeout(popupTimer);
      events.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, []);
  useEffect(() => {
    let refreshTimeoutId;

    const scheduleProactiveRefresh = () => {
      const expiresInStr = localStorage.getItem('expiresIn');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!expiresInStr) return;

      const expiresInMs = Number(expiresInStr) * 1000;
      const bufferMs = 60 * 1000;
      const delay = Math.max(expiresInMs - bufferMs, 5000);

      refreshTimeoutId = setTimeout(async () => {
        try {
          const res = await RefreshToken();

          localStorage.setItem('authToken', res.accessToken);
          if (res.expiresIn) {
            localStorage.setItem('expiresIn', res.expiresIn);
          }
          scheduleProactiveRefresh();
        } catch (err) {
          return err
        }
      }, delay);
    };

    scheduleProactiveRefresh();

    return () => clearTimeout(refreshTimeoutId);
  }, [location.pathname]); 

  const handleContinueSession = async () => {
    try {
      const res = await RefreshToken(); 

      localStorage.setItem('authToken', res.accessToken);
      setShowSessionPopup(false);

    } catch (error) {
      handleLogout();
    }
  };

  const getSidebarItemFromPath = (pathname) => {
    const routeMap = {
      '/dashboard': 'client',
      '/client-dashboard': 'client',
      '/maintenance': 'maintainance',
      '/talliesSection': 'tallies',
      '/reports': 'reports',
      '/job-module': 'JobsModule',
      '/package-costs-details': 'JobsModule',
      '/postage-details': 'JobsModule',
      '/backend-costs-details': 'JobsModule',
      '/lists-details': 'JobsModule'
    };

    if (pathname.startsWith('/job-details/')) {
      return 'JobsModule';
    }

    return routeMap[pathname] || 'client';
  };

  useEffect(() => {
    const currentSidebarItem = getSidebarItemFromPath(location.pathname);
    setActiveSidebarItem(currentSidebarItem);
  }, [location.pathname]);

  const isAuthPage = location.pathname === '/' ||
    location.pathname === '/verifyOtp' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/verifyOtp-forgot' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/unauthorized';

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  const handleLogout = async () => {
    try {
      const authToken = localStorage.getItem('authToken');

      // ✅ CHANGED: 'userToken' fallback hataya — SignIn.js mein yeh key
      // kabhi set nahi hoti, isliye check karna hi bemani tha.
      if (authToken) {
        try {
          // Uncomment if you have a backend logout endpoint
          // await axios.post('/api/auth/logout', {}, {
          //   headers: { 
          //     'Authorization': `Bearer ${authToken}` 
          //   }
          // });
        } catch (apiError) {
          console.log('Backend logout failed, continuing with client cleanup...');
        }
      }

      localStorage.clear();
      sessionStorage.clear();

      document.cookie.split(';').forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });

      if (window.axios && window.axios.defaults && window.axios.defaults.headers.common) {
        delete window.axios.defaults.headers.common['Authorization'];
        delete window.axios.defaults.headers.common['sentryUserId'];
        delete window.axios.defaults.headers.common['name'];
      }

      if (window.userData) delete window.userData;
      if (window.authToken) delete window.authToken;

      const maxId = window.setTimeout(() => { }, 0);
      for (let i = 0; i < maxId; i++) {
        window.clearTimeout(i);
        window.clearInterval(i);
      }

      setActiveSidebarItem('client');

      navigate('/', {
        replace: true,
        state: {
          logout: true,
          timestamp: Date.now()
        }
      });

      window.scrollTo(0, 0);

      window.dispatchEvent(new Event('userLoggedOut'));



    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    const checkAuth = () => {
      // ✅ CHANGED: 'userToken' hataya — sirf 'authToken' ya session check karo
      const hasToken = localStorage.getItem('authToken') ||
        sessionStorage.getItem('userSession');

      const protectedRoutes = ['/dashboard', '/client-dashboard', '/maintenance', '/talliesSection', '/reports', '/job-module'];

      const isJobDetailsRoute = location.pathname.startsWith('/job-details/');

      if (!hasToken && (protectedRoutes.includes(location.pathname) || isJobDetailsRoute)) {
        console.log('No auth token found, redirecting to login...');
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [location.pathname, navigate]);

  const handleSidebarItemClick = (itemId) => {
    setActiveSidebarItem(itemId);
    if (itemId === 'client') {
      navigate('/dashboard');
    } else if (itemId === 'maintainance') {
      navigate('/maintenance');
    } else if (itemId === 'tallies') {
      navigate('/talliesSection');
    } else if (itemId === 'reports') {
      navigate('/reports');
    } else if (itemId === 'JobsModule') {
      navigate('/job-module', {
        state: {
          source: 'job-module',
          isAllClients: false,
          clientId: null,
          clientName: null
        }
      });

      sessionStorage.removeItem('jobDetailsSource');
      sessionStorage.removeItem('isAllClientsFlow');
    }
  };

  return (
    <>
      <div className="app-container">
        {!isAuthPage && <Navbar onLogout={handleLogoutClick} />}

        <div className="app-layout">
          {!isAuthPage && (
            <Sidebar
              activeItem={activeSidebarItem}
              onItemClick={handleSidebarItemClick}
            />
          )}

          <main className={`main-content ${!isAuthPage ? 'with-sidebar' : ''}`}>
            <Routes>
              <Route path="/" element={<SignIn />} />
              <Route path="/verifyOtp" element={<VerifyOtp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verifyOtp-forgot" element={<VerifyOtpForgot />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredPermission="ClientPortal.View">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/client-dashboard"
                element={
                  <ProtectedRoute requiredPermission="ClientPortal.View">
                    <ClientDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/maintenance"
                element={
                  <ProtectedRoute requiredPermission="Maintenance.View">
                    <Maintenance />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/talliesSection"
                element={
                  <ProtectedRoute anyOf={['Tallies.View', 'LoL.View']}>
                    <TalliesSidebar />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <ProtectedRoute requiredPermission="Reports.View">
                    <Reports />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/job-module"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <JobsModule />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/job-details/:jobId"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <JobDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/package-costs-details"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <PackageCostsDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/postage-details"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <PostageDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/backend-costs-details"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <BackendCostsDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/lists-details"
                element={
                  <ProtectedRoute requiredPermission="Job.View">
                    <Lists />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
      </div>

      {showLogoutPopup && (
        <>
          <SuccessMessage
            message="Are you sure you want to logout?"
            type="error"
            onClose={() => {
              setShowLogoutPopup(false);
              handleLogout();
            }}
          />

          <div className="logout-cancel-wrapper">
            <button
              className="logout-cancel-btn"
              onClick={() => setShowLogoutPopup(false)}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* 
      {showSessionPopup && (
        <div className="session-popup">
          <div className="popup-box">
            <h3>Session Expiring Soon</h3>
            <p>Your session will expire in 5 minutes. Do you want to continue?</p>

            <div className="popup-buttons">
              <button className='btn btn-continue' onClick={handleContinueSession}>
                Continue Session
              </button>
              <button className='btn btn-logout' onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};

export default App;